/// <reference lib="es2022.intl" />
import { rawTimeZones } from '@vvo/tzdb';

export const DEFAULT_EVENT_TIME_ZONE = 'Europe/London';

const availableZones = new Set(Intl.supportedValuesOf('timeZone'));
const metadataByZone = new Map(rawTimeZones.flatMap(zone => zone.group.map(id => [id, zone] as const)));
const countryAliases: Record<string, string[]> = {
    GB: ['UK', 'Britain', 'Great Britain', 'British'],
    US: ['USA', 'United States of America'],
};

export function eventTimeZoneLabel(zone: string): string {
    if (zone === 'UTC' || zone === 'Etc/UTC') return 'Coordinated Universal Time (UTC)';
    const metadata = metadataByZone.get(zone);
    if (!metadata) return zone.replace(/_/g, ' ');
    const displayZone = availableZones.has(zone) ? zone : metadata.name;
    const city = displayZone.slice(displayZone.lastIndexOf('/') + 1).replace(/_/g, ' ');
    return `${city} — ${metadata.countryName}`;
}

export const eventTimeZones = [
    DEFAULT_EVENT_TIME_ZONE,
    ...[...availableZones]
        .filter(zone => zone !== DEFAULT_EVENT_TIME_ZONE)
        .sort((a, b) => eventTimeZoneLabel(a).localeCompare(eventTimeZoneLabel(b), 'en-GB')),
    'UTC',
];

export function eventTimeZoneSearchText(zone: string): string {
    const metadata = metadataByZone.get(zone);
    if (!metadata) return `${eventTimeZoneLabel(zone)} ${zone}`;
    const representative = availableZones.has(metadata.name)
        ? metadata.name
        : metadata.group.find(id => availableZones.has(id));
    const citiesAndAliases = zone === representative
        ? [...metadata.mainCities, ...metadata.group.filter(id => !availableZones.has(id))].join(' ')
        : '';
    return `${zone} ${[
        eventTimeZoneLabel(zone), zone, metadata.alternativeName, metadata.countryCode,
        metadata.abbreviation, metadata.dstAbbreviation, citiesAndAliases,
        ...(countryAliases[metadata.countryCode] ?? []),
    ].join(' ').replace(/_/g, ' ')}`;
}

const timeFormatters = new Map<string, Intl.DateTimeFormat | null>();

export interface EventDayHours {
    isTBD?: boolean;
    eventSessions?: { startTime: string; endTime: string }[];
}

/** "09:00 - 17:00" when every scheduled day runs the same hours (none overnight); otherwise null. */
function sharedDailyHours(days?: EventDayHours[]): string | null {
    const windows = (days ?? [])
        .filter(day => !day.isTBD && day.eventSessions?.length)
        .map(day => {
            const starts = day.eventSessions!.map(session => session.startTime.slice(0, 5)).sort();
            const ends = day.eventSessions!.map(session => session.endTime.slice(0, 5)).sort();
            return `${starts[0]} - ${ends[ends.length - 1]}`;
        });
    const [first] = windows;
    if (windows.length < 2 || windows.some(window => window !== first)) return null;
    const [from, to] = first.split(' - ');
    return to > from ? first : null;
}

/** "09:00 - 17:00 BST · Europe/London"; null for date-only events and zones this browser cannot format. */
export function formatEventTimeRange(
    startUtc?: string, endUtc?: string, timeZoneId?: string, eventDays?: EventDayHours[],
): string | null {
    if (!startUtc || !endUtc || !timeZoneId) return null;
    try {
        const start = new Date(startUtc);
        const end = new Date(endUtc);
        const time = new Intl.DateTimeFormat('en-GB', { timeZone: timeZoneId, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
        if (time.format(start) === '00:00' && time.format(end) === '00:00') return null;
        const day = new Intl.DateTimeFormat('en-GB', { timeZone: timeZoneId, day: 'numeric', month: 'short' });
        const spansDays = day.format(start) !== day.format(end);
        const withDay = (value: Date) => spansDays ? `${day.format(value)}, ${time.format(value)}` : time.format(value);
        const zoneName = new Intl.DateTimeFormat('en-GB', { timeZone: timeZoneId, timeZoneName: 'short' })
            .formatToParts(start).find(part => part.type === 'timeZoneName')?.value;
        const suffix = `${zoneName ? ` ${zoneName}` : ''} \u00b7 ${timeZoneId.replace(/_/g, ' ')}`;
        const daily = spansDays ? sharedDailyHours(eventDays) : null;
        if (daily) return `Daily ${daily}${suffix}`;
        // Differing hours per day cannot be summarised here, so only the zone is given.
        if (spansDays && eventDays?.length !== 1) return `Times in ${timeZoneId.replace(/_/g, ' ')}${zoneName ? ` (${zoneName})` : ''}`;
        return `${withDay(start)} - ${withDay(end)}${suffix}`;
    } catch (error) {
        if (!(error instanceof RangeError)) throw error;
        return null;
    }
}

export function eventTimeZoneTimeNow(zone: string, now: Date): string | null {
    let formatter = timeFormatters.get(zone);
    if (formatter === undefined) {
        try {
            formatter = new Intl.DateTimeFormat('en-GB', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
        } catch (error) {
            if (!(error instanceof RangeError)) throw error;
            console.warn('Cannot preview the saved event time zone in this browser:', zone, error);
            formatter = null;
        }
        timeFormatters.set(zone, formatter);
    }
    return formatter?.format(now) ?? null;
}
